<template>
    <NuxtLayout>
        <NuxtPage />
        <Teleport
            :disabled="teleportNotificationArea === false"
            :to="teleportNotificationArea ? '#notificationAreaRoot' : null">
            <NotificationArea position="top-right" />
        </Teleport>
        <CookieControl :locale="locale" />
    </NuxtLayout>
</template>

<script setup>
import {usePageTitle} from "@/composables/usePageTitle";

const {t, te, locale} = useI18n()
const route = useRoute()
const {public: publicConfig} = useRuntimeConfig()
const pageTitle = usePageTitle()

const title = computed(() => {
    // custom title from pageTitle, has to be set through global state in order to be reactive (https://github.com/nuxt/nuxt/issues/13463)
    if (pageTitle.value) return pageTitle.value
    // global default title from translation file
    if (route.name) return te(`pageTitle.${route.name}`) ? t(`pageTitle.${route.name}`) : false
})

useHead(() => ({
    title: (title.value) ? `${publicConfig.appName} - ${title.value}` : publicConfig.appName
}))

const teleportNotificationArea = useState('teleportNotificationArea', () => false)

const {
    cookiesEnabledIds
} = useCookieControl()


watch(() => cookiesEnabledIds.value, (current, previous) => {
    (current?.includes('meta-pixel')) ? enableMetaPixel() : disableMetaPixel();
    (current?.includes('clarity')) ? enableClarity() : disableClarity();
}, { deep: true })

onMounted(() => {
    (cookiesEnabledIds.value?.includes('meta-pixel')) ? enableMetaPixel() : disableMetaPixel();
    (cookiesEnabledIds.value?.includes('clarity')) ? enableClarity() : disableClarity();
})

function enableMetaPixel() {
    // --- Meta Pixel ---
    if (!window.fbq) {
        const initFbq = function(f,b,e,v,n,t,s) {
            if(f.fbq)return;
            n=f.fbq=function(){
                n.callMethod? n.callMethod.apply(n,arguments):n.queue.push(arguments)
            };
            if(!f._fbq)f._fbq=n;
            n.push=n;
            n.loaded=!0;
            n.version='2.0';
            n.queue=[];
            t=b.createElement(e);
            t.async=!0;
            t.src=v;
            s=b.getElementsByTagName(e)[0];
            document.head.appendChild(t);
        }

        initFbq(window, document,'script', 'https://connect.facebook.net/en_US/fbevents.js');

        fbq('init', '1125168786396484');
        fbq('track', 'PageView');
    }

    // Noscript for Meta Pixel
    if (!document.getElementById('meta-pixel-noscript')) {
        const noscript = document.createElement('noscript');
        noscript.id = 'meta-pixel-noscript';
        noscript.innerHTML = `
            <img height="1" width="1" style="display:none"
                src="https://www.facebook.com/tr?id=1125168786396484&ev=PageView&noscript=1" alt=""/>
        `;
        document.body.appendChild(noscript);
    }
}

function disableMetaPixel() {
    // Meta Pixel
    document.querySelectorAll('script[src*="connect.facebook.net"]').forEach(el => el.remove());

    const metaPixelNoscript = document.getElementById('meta-pixel-noscript');
    if (metaPixelNoscript) metaPixelNoscript.remove();

    if (window.fbq) delete window.fbq;
    if (window._fbq) delete window._fbq;

    // delete Cookies
    ['_fbp', '_fbc', 'datr', 'wd'].forEach(name => {
        const hostname = window.location.hostname;
        const domains = [
            hostname,
            '.' + hostname.replace(/^www\./, '')
        ];
        domains.forEach(domain => {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; SameSite=Lax; Secure;`;
        });
        // fallback ohne Domain
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
    });
}

function enableClarity() {
    if (!window.clarity && !document.querySelector('script[src*="clarity.ms/tag/"]')) {
        window.clarity = function() {
            (window.clarity.q = window.clarity.q || []).push(arguments);
        };
        const script = document.createElement('script');
        script.src = 'https://www.clarity.ms/tag/tjfqik10rx';
        script.async = true;
        document.head.appendChild(script);
    }
}

function disableClarity() {
    document.querySelectorAll('script[src*="clarity.ms/tag/"]').forEach(el => el.remove());
    document.querySelectorAll('script[src*="https://scripts.clarity.ms/"]').forEach(s => s.remove());

    if (window.clarity) delete window.clarity;

    // delete Cookies
    ['CLID', 'MUID'].forEach(name => {
        const hostname = window.location.hostname;
        const domains = [
            hostname,
            '.' + hostname.replace(/^www\./, '')
        ];
        domains.forEach(domain => {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; Secure;`;
        });
        // fallback ohne Domain
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
    });
}

</script>

<style>
.cookieControl__ModalContent {
    @apply max-w-[800px]
}
.cookieControl__Bar  {
     @apply shadow-[0_-1px_10px_0_#0000004d;];
 }

.cookieControl__ModalContent h2 {
    @apply text-xl font-bold mt-8;
}

.cookieControl__ModalContent ul ul {
    @apply list-none p-0;
}

.cookieControl__ModalButtons button:first-of-type {
    @apply grow;
}

button.cookieControl__ModalClose {
    @apply bg-white text-black p-0 font-bold hover:bg-white hover:text-primary;
}
</style>